import { createContext, useContext, useState, useEffect, type ReactNode, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PublicKey } from '@solana/web3.js';
// Remove direct Privy import - lazy load it instead
// import { usePrivy } from '@privy-io/react-auth';

import api from '../services/api';
import { phantomWallet } from '../utils/phantomWallet';
import type { User } from '../types/user';
import NotificationService from '../services/NotificationService';
import FriendsService from '../services/FriendsService';

// Lazy load the Privy integration
const LazyPrivyIntegration = lazy(() => import('../hooks/usePrivyIntegration'));

interface UserProviderProps {
  children: ReactNode;
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (userData: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => void;
  updateUser: (updatedUser: User) => void;
  unreadNotifications: number;
  refreshNotificationCount: () => void;
  pendingFriendRequests: number;
  refreshPendingFriendRequests: () => void;
  // Authentication method tracking
  authMethod: 'phantom' | 'privy' | null;
  // Wallet switching
  switchToPhantom: () => Promise<void>;
  switchToPrivy: () => Promise<void>;
  // Logout tracking
  justLoggedOut: boolean;
  clearLogoutFlag: () => void;
}

// Create a default context to ensure we never return undefined
const defaultContext: UserContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: () => {},
  logout: async () => {},
  updateUser: () => {},
  refreshUser: () => {},
  unreadNotifications: 0,
  refreshNotificationCount: () => {},
  pendingFriendRequests: 0,
  refreshPendingFriendRequests: () => {},
  authMethod: null,
  switchToPhantom: async () => {},
  switchToPrivy: async () => {},
  justLoggedOut: false,
  clearLogoutFlag: () => {},
};

const UserContext = createContext<UserContextType>(defaultContext);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

function UserProviderContent({ children }: UserProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [pendingFriendRequests, setPendingFriendRequests] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<'phantom' | 'privy' | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Lazy-loaded Privy integration state
  const [privyData, setPrivyData] = useState<any>(null);
  const [loadPrivy, setLoadPrivy] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [justLoggedOut, setJustLoggedOut] = useState(() => {
    // Initialize from localStorage to persist across page reloads
    const isLoggedOut = localStorage.getItem('justLoggedOut') === 'true';
    const logoutTimestamp = localStorage.getItem('justLoggedOutTimestamp');
    
    // Clear the flag if it's been more than 1 hour since logout
    if (isLoggedOut && logoutTimestamp) {
      const hoursSinceLogout = (Date.now() - parseInt(logoutTimestamp)) / (1000 * 60 * 60);
      if (hoursSinceLogout > 1) {
        localStorage.removeItem('justLoggedOut');
        localStorage.removeItem('justLoggedOutTimestamp');
        return false;
      }
    }
    
    return isLoggedOut;
  });
  
  // Extract Privy properties (will be null until Privy loads)
  const authenticated = privyData?.authenticated || false;
  const privyUser = privyData?.user || null;
  const privyLogout = privyData?.logout;
  // const privyLogin = privyData?.login;
  
  // Load Privy if the user was previously authenticated with it
  useEffect(() => {
    const savedAuthMethod = localStorage.getItem('authMethod');
    if (savedAuthMethod === 'privy' && !loadPrivy) {
      setLoadPrivy(true);
    }
  }, [loadPrivy]);



  const fetchUnreadCount = async () => {
    try {
      const notifications = await NotificationService.getUnreadNotifications();
      setUnreadNotifications(notifications.length);
    } catch (error) {
      console.error('Failed to fetch unread notifications:', error);
    }
  };

  const fetchPendingFriendRequests = async () => {
    try {
      const requests = await FriendsService.getPendingRequests();
      setPendingFriendRequests(requests.length);
    } catch (error) {
      console.error('Failed to fetch pending friend requests:', error);
    }
  };

  const refreshNotificationCount = () => {
    fetchUnreadCount();
  };

  const refreshPendingFriendRequests = () => {
    fetchPendingFriendRequests();
  };

  const refreshUser = () => {
    const initializeSession = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Check if we have a stored token (including refreshToken)
        const accessToken = localStorage.getItem('accessToken');
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');
        const storedToken = accessToken || token || refreshToken;
        
        if (storedToken) {
          console.log('🔑 Token found, attempting to restore session...');
          try {
            const response = await api.get('/api/dashboard');
            if (response.data.success && response.data.data.user) {
              console.log('✅ Session restored successfully:', response.data.data.user.handle);
              setUser(response.data.data.user);
              
              // Detect authentication method based on user data
              if (response.data.data.user.privyId) {
                setAuthMethod('privy');
              } else if (response.data.data.user.walletAddress) {
                setAuthMethod('phantom');
              }
              
              return; // Exit early on success
            } else {
              console.log('❌ Session restoration failed - invalid response');
            }
          } catch (error) {
            console.error('❌ Session restoration failed:', error);
          }
          
          // If we get here, session restoration failed - clear tokens
          localStorage.removeItem('accessToken');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      } catch (error: unknown) {
        console.error('❌ Initialization error:', error);
        setError('Failed to initialize. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  };

  // Session restoration and wallet setup
  useEffect(() => {
    refreshUser();

    const handleAccountChange = async (newPublicKey: PublicKey | null, oldPublicKey: PublicKey | null) => {
      const currentPath = window.location.pathname;
      console.log('🔄 Account change detected:', {
        old: oldPublicKey?.toBase58(),
        new: newPublicKey?.toBase58(),
        currentPath
      });

      // Always process account changes - simplified logic
      const hasAccountChanged = (oldPublicKey?.toBase58() !== newPublicKey?.toBase58());
      
      if (!hasAccountChanged) {
        console.log('🔄 Same account, ignoring');
        return;
      }

      console.log('🔄 Processing account switch...');

      // Clear current user session immediately
      setUser(null);
      setUnreadNotifications(0);
      setPendingFriendRequests(0);
      setAuthMethod(null);

      // Clear any stored auth tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('pendingWalletData');
      
      // Clear registration state if any
      sessionStorage.removeItem('registrationData');
      sessionStorage.removeItem('walletConnectionData');

      // Determine if we should redirect to auth page
      const isOnHomepage = currentPath === '/';
      const isOnPublicSocialCard = currentPath.startsWith('/@') || currentPath.startsWith('/u/') || currentPath.startsWith('/card/');
      const isOnAuthPage = currentPath.startsWith('/auth');

      // Always redirect to auth page if on dashboard or protected routes
      if (currentPath.startsWith('/dashboard') || (!isOnHomepage && !isOnPublicSocialCard && !isOnAuthPage)) {
        console.log('🔄 Redirecting to auth page due to account switch');
        
        navigate('/auth', { 
          replace: true,
          state: { 
            message: 'Account switched. Please connect with your new wallet.',
            accountSwitched: true
          }
        });
      } else if (isOnHomepage) {
        // For homepage, just refresh to show logged out state
        console.log('🔄 Refreshing homepage after account switch');
        window.location.reload();
      }
    };

    const handleAccountDisconnect = async () => {
      console.log('🔌 Account disconnected');
      
      if (user) {
        await logout();
      }
      
      // Clear all stored data (logout should handle this, but ensure it's cleared)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('pendingWalletData');
      localStorage.removeItem('authMethod');
      sessionStorage.removeItem('registrationData');
      sessionStorage.removeItem('walletConnectionData');

      // If on a protected page, redirect to auth
      const currentPath = location.pathname;
      const isOnHomepage = currentPath === '/';
      const isOnPublicSocialCard = currentPath.startsWith('/@') || currentPath.startsWith('/u/') || currentPath.startsWith('/card/');
      const isOnDashboard = currentPath.startsWith('/dashboard');

      if (isOnDashboard || (!isOnHomepage && !isOnPublicSocialCard)) {
        navigate('/auth', { replace: true });
      }
    };

    // Set up wallet event listeners
    phantomWallet.setAccountChangeCallback(handleAccountChange);
    phantomWallet.setDisconnectCallback(handleAccountDisconnect);

    // Cleanup event listeners on unmount
    return () => {
      phantomWallet.removeAccountChangeCallback();
      phantomWallet.removeDisconnectCallback();
    };
  }, [navigate]);

  // Handle Privy authentication changes
  useEffect(() => {
    if (authenticated && privyUser && !user) {
      // Privy user authenticated but no user in our context - this happens during login
      console.log('🔐 Privy authenticated, checking our system...');
      // Let the AuthFlow handle this
    } else if (!authenticated && user && authMethod === 'privy') {
      // Privy user logged out
      console.log('🔐 Privy user logged out');
      logout();
    }
  }, [authenticated, privyUser, user, authMethod]);

  useEffect(() => {
    if (user) {
      refreshNotificationCount();
      refreshPendingFriendRequests();
      // Poll for new notifications and friend requests every 30 seconds
      const intervalId = setInterval(() => {
        refreshNotificationCount();
        refreshPendingFriendRequests();
      }, 30000);
      return () => clearInterval(intervalId);
    }
  }, [user]);

  // Refresh user data when dashboard loads to ensure we have latest socialCard data
  useEffect(() => {
    if (user && !user.socialCard) {
      console.log('🔄 Refreshing user data to get socialCard...');
      refreshUser();
    }
  }, [user]);

  const switchToPhantom = async () => {
    try {
      // Logout from Privy if connected
      if (authenticated) {
        await privyLogout();
      }
      
      // Clear current session
      await logout();
      
      // Redirect to auth page for Phantom connection
      navigate('/auth', { 
        replace: true,
        state: { 
          message: 'Please connect your Phantom wallet.',
          switchedToPhantom: true
        }
      });
    } catch (error) {
      console.error('Error switching to Phantom:', error);
    }
  };

  const switchToPrivy = async () => {
    try {
      // Disconnect from Phantom if connected
      if (phantomWallet.connected) {
        await phantomWallet.disconnect();
      }
      
      // Clear current session
      await logout();
      
      // Load Privy and trigger login
      setLoadPrivy(true);
      setIsAuthModalOpen(true);
    } catch (error) {
      console.error('Error switching to Privy:', error);
    }
  };
  
  // Handle Privy ready callback
  const handlePrivyReady = (privyInstance: any) => {
    setPrivyData(privyInstance);
    setIsAuthModalOpen(false);
  };
  
  // Handle Privy logout callback
  const handlePrivyLogout = () => {
    setPrivyData(null);
  };

  const login = (userData: User) => {
    console.log('✅ User logged in:', userData.handle);
    setUser(userData);
    setError(null);
  };

  const logout = async () => {
    console.log('👋 Logging out user');
    
    // Set flag to indicate intentional logout and persist it with timestamp
    setJustLoggedOut(true);
    localStorage.setItem('justLoggedOut', 'true');
    localStorage.setItem('justLoggedOutTimestamp', Date.now().toString());
    console.log('🔐 Set logout flag to prevent auto-login');
    
    try {
      // Clear backend session
      await api.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    }
    
    // Clear local state
    setUser(null);
    setUnreadNotifications(0);
    setPendingFriendRequests(0);
    setAuthMethod(null);
    
    // Clear stored tokens and data
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('pendingWalletData');
    localStorage.removeItem('authMethod'); // Clear saved auth method
    sessionStorage.removeItem('registrationData');
    sessionStorage.removeItem('walletConnectionData');
    
    // AGGRESSIVELY clear ALL possible Privy-related storage
    console.log('🧹 Aggressively clearing ALL Privy browser data...');
    
    // Clear all localStorage items that might be Privy-related
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('privy') || key.includes('Privy') || key.includes('PRIVY'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      console.log('🧹 Removing localStorage key:', key);
      localStorage.removeItem(key);
    });
    
    // Clear all sessionStorage items that might be Privy-related  
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('privy') || key.includes('Privy') || key.includes('PRIVY'))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => {
      console.log('🧹 Removing sessionStorage key:', key);
      sessionStorage.removeItem(key);
    });
    
    // Clear cookies that might be Privy-related
    document.cookie.split(";").forEach(function(c) { 
      const cookie = c.replace(/^ +/, "");
      if (cookie.includes('privy') || cookie.includes('Privy') || cookie.includes('PRIVY')) {
        console.log('🧹 Removing cookie:', cookie);
        document.cookie = cookie.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      }
    });
    
    // Disconnect from wallets if connected
    if (phantomWallet.connected) {
      try {
        await phantomWallet.disconnect();
      } catch (error) {
        console.warn('Failed to disconnect Phantom wallet:', error);
      }
    }
    
    // Handle Privy logout
    if (authenticated && privyLogout) {
      try {
        console.log('🔐 Logging out from Privy...');
        await privyLogout();
        // Reset Privy state after logout
        setPrivyData(null);
        setLoadPrivy(false);
        setIsAuthModalOpen(false);
      } catch (error) {
        console.warn('Failed to logout from Privy:', error);
      }
    } else {
      // Even if Privy isn't authenticated, ensure we clear the state
      setPrivyData(null);
      setLoadPrivy(false);
      setIsAuthModalOpen(false);
    }
    
    console.log('✅ Logout complete - user must re-authenticate to access account');
    setError(null);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const clearLogoutFlag = () => {
    setJustLoggedOut(false);
    localStorage.removeItem('justLoggedOut');
    localStorage.removeItem('justLoggedOutTimestamp');
    console.log('🔑 Cleared logout flag - user can now auto-login again');
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    updateUser,
    refreshUser,
    unreadNotifications,
    refreshNotificationCount,
    pendingFriendRequests,
    refreshPendingFriendRequests,
    authMethod,
    switchToPhantom,
    switchToPrivy,
    justLoggedOut,
    clearLogoutFlag,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
      {loadPrivy && (
        <Suspense fallback={null}>
          <LazyPrivyIntegration
            onPrivyReady={handlePrivyReady}
            onLogout={handlePrivyLogout}
            isAuthModalOpen={isAuthModalOpen}
          />
        </Suspense>
      )}
    </UserContext.Provider>
  );
}

export function UserProvider({ children }: UserProviderProps) {
  return (
    <UserProviderContent>
      {children}
    </UserProviderContent>
  );
} 